import { type FormEvent, useEffect, useState } from 'react';

import { errorMessage } from '@/shared/api/errors';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';

import { daysLabel, KINDS, kindLabel } from '@/shared/documents/kinds';

import { usePutRetentionPolicy, useRetentionPolicy } from './useAdmin';

/**
 * How long each kind of document is kept (contract v10.5). The three kinds the law
 * covers show their floor and refuse to go under it, here and in the API. One line says
 * the floor and the trash; the rest is in the header's help.
 */
export function DocumentsTab() {
  const policy = useRetentionPolicy();
  const put = usePutRetentionPolicy();
  const [days, setDays] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (policy.data) {
      setDays(
        Object.fromEntries(
          Object.entries(policy.data.days).map(([kind, value]) => [kind, String(value)]),
        ),
      );
    }
  }, [policy.data]);

  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    const payload: Record<string, number> = {};
    for (const [kind, value] of Object.entries(days)) {
      const parsed = Number.parseInt(value, 10);
      if (Number.isFinite(parsed)) payload[kind] = parsed;
    }
    put.mutate(payload, { onSuccess: () => setSaved(true) });
  }

  return (
    <div className="flex flex-col gap-4">
      {policy.isPending ? <Spinner variant="block" label="Cargando la política" /> : null}
      {policy.isError ? (
        <ErrorMessage error={policy.error}>No se pudo cargar la conservación</ErrorMessage>
      ) : null}
      {policy.data ? (
        <>
          <Callout compact tone="info">
            Facturas de proveedor, albaranes y presupuestos emitidos:{' '}
            <strong>{daysLabel(policy.data.legal_min_days)} como mínimo</strong>, por ley. Lo que se
            borra pasa {policy.data.trash_days} días en la papelera.
          </Callout>
          <Card title="Cuánto se conserva cada tipo de documento">
            <form onSubmit={submit} noValidate className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {KINDS.map((kind) => {
                const legal = policy.data.legal_kinds.includes(kind);
                const value = days[kind] ?? '';
                const parsed = Number.parseInt(value, 10);
                return (
                  <Input
                    key={kind}
                    label={kindLabel(kind)}
                    type="number"
                    min={legal ? policy.data.legal_min_days : 1}
                    value={value}
                    onChange={(event) => setDays({ ...days, [kind]: event.target.value })}
                    hint={
                      <span className="flex items-center gap-1.5">
                        {Number.isFinite(parsed) ? daysLabel(parsed) : 'días'}
                        {legal ? <Badge tone="info">mínimo legal</Badge> : null}
                      </span>
                    }
                  />
                );
              })}
              <div className="flex items-center gap-3 sm:col-span-2 lg:col-span-4">
                <Button type="submit" size="sm" disabled={put.isPending}>
                  {put.isPending ? 'Guardando…' : 'Guardar la política'}
                </Button>
                {saved ? (
                  <span role="status" className="text-ok text-[13.5px]">
                    Guardado.
                  </span>
                ) : null}
                {put.isError ? (
                  <span role="alert" className="text-danger text-[13.5px]">
                    {errorMessage(put.error)}
                  </span>
                ) : null}
              </div>
            </form>
          </Card>
        </>
      ) : null}
    </div>
  );
}
